import axios from "axios";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import address from "@/apis/address";
import { TokenKey } from "@/constants/auth";
import { CustomApiError } from "./error";
import { notifyError } from "./notification";

// 创建 axios 实例
const request = axios.create({
  baseURL: address.srm,
  timeout: 60000, // 请求超时时间
});

// 请求拦截器
request.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // 在请求头中添加 token
    const token = localStorage.getItem(TokenKey);
    if (token) {
      config.headers!["Authorization"] = `Bearer ${token}`;
    }

    // 添加调试日志
    console.log(
      `[🌐 HTTP 请求] ${config.method?.toUpperCase()} ${config.url}`,
      {
        baseURL: config.baseURL,
        url: config.url,
        method: config.method,
        data: config.data,
        headers: config.headers,
      },
    );

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  },
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 根据后端约定的数据结构处理响应
    const { data, status, config } = response;

    console.log(
      `[✅ HTTP 响应成功] ${config.method?.toUpperCase()} ${config.url}`,
      {
        httpStatus: status,
        responseCode: data.code,
        responseData: data,
      },
    );

    // 这里可以根据实际后端返回的数据结构进行调整
    if (data.code === 200) {
      return response;
    } else if (data.code === 401) {
      const hasToken = localStorage.getItem(TokenKey) !== null;
      hasToken && notifyError(data.message || "请求失败");
      return Promise.reject(new CustomApiError(data.message, response));
    } else {
      console.warn(
        `[⚠️  业务状态非成功] 状态码: ${data.code}, 消息: ${data.message}`,
      );
      notifyError(data.message || "请求失败");
      return Promise.reject(new CustomApiError(data.message, response));
    }
  },
  (error) => {
    // 处理错误状态码
    if (error.response) {
      const { status, data, config } = error.response;

      console.error(
        `[❌ HTTP 响应错误] ${config.method?.toUpperCase()} ${config.url}`,
        {
          httpStatus: status,
          responseCode: data?.code,
          responseData: data,
          error: error.message,
        },
      );

      switch (status) {
        case 401:
          notifyError("登录已过期，请重新登录");
          break;
        case 403:
          notifyError("没有权限访问");
          break;
        case 404:
          notifyError("请求的资源不存在");
          break;
        case 500:
          notifyError("服务器内部错误");
          break;
        default:
          notifyError(data.message || "请求失败");
      }
    } else if (error.request) {
      console.error(`[❌ 网络请求失败] 没有收到响应`, error);
      notifyError("网络连接失败，请检查网络");
    } else {
      console.error(`[❌ 请求配置错误]`, error.message);
      notifyError(error.message || "请求失败");
    }

    return Promise.reject(error);
  },
);

export default request;

// 导出类型定义
export type { AxiosRequestConfig, AxiosResponse };
